// =============================================================================
// FILE: packages/renderer/src/components/calibration/CalibrationIndicator.tsx
// PURPOSE: Top-bar status badge for the CalibrationEngine. Click expands a
//          small panel that lists every check + a Re-run button + an Export
//          JSON button. Never blocks the UI.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Download } from 'lucide-react';
import {
  calibrationEngine,
  type CalibrationReport,
} from '../../lib/calibration/CalibrationEngine.js';

type Tone = 'ok' | 'warn' | 'fail' | 'pending';

function reportTone(r: CalibrationReport | null): Tone {
  if (!r) return 'pending';
  if (r.counts.critical > 0 || r.counts.fail > 0) return 'fail';
  if (r.counts.warn > 0) return 'warn';
  return 'ok';
}

const TONE_META: Record<Tone, { dot: string; label: string; ring: string }> = {
  ok:      { dot: 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,.7)]', label: 'System OK',         ring: 'border-emerald-400/50' },
  warn:    { dot: 'bg-amber-400   shadow-[0_0_6px_rgba(251,191,36,.7)]', label: 'Warnings',         ring: 'border-amber-400/50'   },
  fail:    { dot: 'bg-rose-400    shadow-[0_0_6px_rgba(251,113,133,.7)]', label: 'Critical failure', ring: 'border-rose-400/50'    },
  pending: { dot: 'bg-zinc-500',                                          label: 'Calibrating…',     ring: 'border-zinc-700'      },
};

export function CalibrationIndicator(): JSX.Element {
  const [report, setReport] = useState<CalibrationReport | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ranRef = useRef(false);

  const runOnce = async (): Promise<void> => {
    setBusy(true);
    try {
      const r = await calibrationEngine.run();
      setReport(r);
    } finally { setBusy(false); }
  };

  // Run exactly once after mount. Non-blocking: a slow check cannot stall
  // anything because each call has a per-check timeout.
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    // 250ms idle so the first render isn't taxed.
    const id = setTimeout(() => void runOnce(), 250);
    return () => clearTimeout(id);
  }, []);

  const tone = reportTone(report);
  const meta = TONE_META[tone];

  const onExport = (): void => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `bim-ops-calibration-${report.ranAt.replace(/[:.]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="calibration-indicator"
        title="Calibration status — click for details"
        onClick={() => setOpen((v) => !v)}
        className={[
          'h-8 inline-flex items-center gap-2 px-2.5 rounded-sm border bg-zinc-900/70 text-[11px] uppercase tracking-[0.16em] text-zinc-200 hover:bg-zinc-800/80 transition',
          meta.ring,
        ].join(' ')}
      >
        <span className={['h-2 w-2 rounded-full', meta.dot, tone === 'ok' ? 'animate-pulse' : ''].join(' ')} />
        <span>{meta.label}</span>
        {report && report.counts.fail + report.counts.warn > 0 && (
          <span data-testid="calibration-count" className="text-zinc-400 font-mono">
            ({report.counts.fail + report.counts.warn})
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="calibration-panel"
          className="absolute right-0 mt-2 w-[440px] max-h-[70vh] overflow-auto rounded-sm border border-zinc-700 bg-zinc-950 shadow-xl z-40 p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">
              Calibration {report ? `· ${report.results.length} checks` : '…'}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="calibration-rerun"
                disabled={busy}
                onClick={() => void runOnce()}
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.16em] rounded-sm border border-zinc-700 text-zinc-200 hover:text-white transition disabled:opacity-40"
              >
                <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} />
                Re-run
              </button>
              <button
                type="button"
                data-testid="calibration-export"
                disabled={!report}
                onClick={onExport}
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.16em] rounded-sm border border-zinc-700 text-zinc-200 hover:text-white transition disabled:opacity-40"
              >
                <Download className="h-3 w-3" />
                JSON
              </button>
            </div>
          </div>
          {!report ? (
            <div data-testid="calibration-pending" className="px-2 py-3 text-[12px] text-zinc-400">
              Running checks…
            </div>
          ) : (
            <ul className="divide-y divide-zinc-800">
              {report.results.map((r) => (
                <li
                  key={r.name}
                  data-testid={`calibration-item-${r.name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                  className="px-2 py-2 flex items-start gap-2"
                >
                  {r.status === 'pass'
                    ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                    : r.status === 'warn'
                      ? <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-300 shrink-0" />
                      : <XCircle className="h-4 w-4 mt-0.5 text-rose-400 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[12px] text-zinc-100">{r.name}</span>
                      <span className="text-[10px] text-zinc-500 font-mono whitespace-nowrap">
                        {r.durationMs}ms
                      </span>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                      {r.category} · {r.severity}
                    </div>
                    {r.error && (
                      <div className="mt-1 text-[11px] text-rose-200 break-words font-mono">
                        {r.error}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
